import React from 'react';
import LayerHelper from '@/util/LayerHelper';
import {
  FROM,
  FROM_SAVED_QUERY,
  GROUP_BY,
  TEMPLATE,
  WHERE,
  AGGREGATE,
} from '@/util/enums/EnumLayers';
import {
  ACTION_FILTER,
  userIsFinishedOnboarding,
} from '@/components/HighlightFeature';
import Layer from '@/components/layers/Layer';
import TemplateLayer from '@/components/layers/TemplateLayer';
import ErrorBoundary from '@/components/ErrorBoundary';
import Step from '@/components/layers/Step';
import { useSelector } from 'react-redux';
import {
  getAliases,
  getLastLayerAdded,
  getLayerErrorMessage,
  getLayerInEditMode,
  getQueryBuilderSteps,
  getSelectedLayerId,
  getUserActions,
} from '@/store-selectors';
import {
  addLayerClicked,
  editLayerClicked,
  layerUserInteraction,
} from '@/actions';
import { safeOnClick } from '@/util/helpers/event-helpers';
import { useAppDispatch } from '@/store/hooks';
import { useInvalidStepId } from '@/features/query-builder/hooks';
import { usePrefetchStepColumns } from '@/features/query-builder/queriesApiSlice';
import { StepContent } from './StepContent';

type QueryStep = Layer;

export type ActiveStepsProps = {
  transitionsOn: boolean;
  lastAddStepButtonsOpened: Nullable<number>;
  setAddStepButtonsOpened: F<Nullable<number>>;
};

export const ActiveSteps: React.FC<ActiveStepsProps> = ({
  transitionsOn,
  lastAddStepButtonsOpened,
  setAddStepButtonsOpened,
}) => {
  const dispatch = useAppDispatch();
  const steps = useSelector(getQueryBuilderSteps);
  const selectedLayerId = useSelector(getSelectedLayerId);
  const stepErrorMessage = useSelector(getLayerErrorMessage);
  const layerInEditMode = useSelector(getLayerInEditMode);
  const lastLayerAdded = useSelector(getLastLayerAdded);
  const aliases = useSelector(getAliases);
  const userActions = useSelector(getUserActions);
  const invalidStepId = useInvalidStepId();

  const lastStepToShowIndex =
    steps.findIndex((s) => s._id === invalidStepId) + 1 || Infinity;

  const handleLayerEditClicked = (
    layerBeingEdited: QueryStep,
    previousLayers: QueryStep[],
  ) => dispatch(editLayerClicked(layerBeingEdited, previousLayers));

  const handleLeaveEditMode = () => dispatch(editLayerClicked(null));

  const handleLayerInteraction = (event: string, props: unknown) => () =>
    dispatch(layerUserInteraction({ event, props }));

  const handleAddLayerClicked = (
    modal: React.ClassicComponent,
    relevantLayers: QueryStep[],
    additionalOptions: unknown,
  ) => dispatch(addLayerClicked(modal, relevantLayers, additionalOptions));

  const stepsHelper = new LayerHelper(steps, selectedLayerId);
  const stepsToShow = invalidStepId
    ? steps.slice(0, lastStepToShowIndex)
    : stepsHelper.activeLayers();

  const lastLayerNotInEditMode = !layerInEditMode
    ? [...stepsToShow]
        .reverse()
        .find(
          (l) =>
            ![FROM, FROM_SAVED_QUERY].includes(l.type()) && !l.isTemplate(),
        )
    : null;

  const llID = lastLayerNotInEditMode?.id();

  const filterWhenOnboarding = (step: QueryStep) => {
    if (userIsFinishedOnboarding(userActions)) {
      return true; // no longer onboarding, so show all steps
    }
    if (step.isTemplate()) {
      switch (step.layerType()) {
        case AGGREGATE:
          return !userActions || userActions[ACTION_FILTER];
        case WHERE:
          return true;
        default:
          return false;
      }
    }
    return true; // we don't hide non-template steps during onboarding
  };

  return (
    <div>
      {stepsToShow
        .filter(filterWhenOnboarding)
        .map((step: QueryStep, i: number) => (
          <StepRenderer
            key={step.id()}
            index={i}
            step={step}
            layerHelper={stepsHelper}
            onAddLayerClicked={handleAddLayerClicked}
            onLayerEditClicked={handleLayerEditClicked}
            onLayerInteraction={handleLayerInteraction}
            aliases={aliases}
            lastLayerAdded={lastLayerAdded}
            layerInEditMode={layerInEditMode}
            llID={llID}
            layerErrorMsg={stepErrorMessage}
            actions={userActions}
            transitionsOn={transitionsOn}
            leaveEditMode={handleLeaveEditMode}
            lastAddStepButtonsOpened={lastAddStepButtonsOpened}
            setAddStepButtonsOpened={setAddStepButtonsOpened}
          />
        ))}
    </div>
  );
};

const StepRenderer: React.FC<any> = ({
  step,
  index,
  layerHelper,
  onAddLayerClicked,
  onLayerEditClicked,
  onLayerInteraction,
  aliases,
  lastLayerAdded,
  layerInEditMode,
  llID,
  layerErrorMsg,
  actions,
  transitionsOn,
  leaveEditMode,
  lastAddStepButtonsOpened,
  setAddStepButtonsOpened,
}) => {
  const prefetchColumns = usePrefetchStepColumns(
    step._id,
    index,
    step._type !== AGGREGATE,
  );

  const handleEditStep = safeOnClick(() => {
    const relevantLayers = layerHelper.relevantLayers(step);
    if (step.type() === TEMPLATE) {
      const payload =
        step.layerType() === GROUP_BY // GROUP_BY = summarize-as-new-column
          ? { layerToReplace: step, aggregate: true }
          : { layerToReplace: step };
      onAddLayerClicked(
        (step as TemplateLayer).modal(),
        relevantLayers,
        payload,
      );
    } else {
      onLayerEditClicked(step, relevantLayers);
    }
  });

  const handleUpdateStep = (updatedStep: QueryStep) => {
    // make sure new layer has same id as existing (otherwise it gets really complicated with caching of columns, etc.)
    onLayerInteraction('updated', {
      updatedLayer: updatedStep.cloneAndUpdateId(step.id()),
    })();
  };

  const stepNumber = index + 1;
  const isLastActiveStep = layerHelper.activeLayers().length === stepNumber;

  const editMode =
    step.isTemplate() ||
    (layerInEditMode && layerInEditMode.id() === step.id());

  const layerContent = !('editMode' in step) ? ( // if layer doesn't yet support editMode, use oldschool buildContent function instead
    step.buildContent(handleEditStep, aliases)
  ) : (
    <StepContent
      editMode={editMode}
      step={step}
      stepIndex={index}
      aliases={aliases}
      onUpdateStep={handleUpdateStep}
      onEditStep={handleEditStep}
    />
  );

  // if last later matches layer then true, else false
  const isLastLayerAdded = lastLayerAdded?.id() === step.id();

  return (
    <ErrorBoundary key={step.id()}>
      <Step
        onMouseEnter={prefetchColumns}
        key={step.id()}
        isEditMode={editMode}
        onLeaveEditMode={leaveEditMode}
        stepNo={stepNumber}
        layer={step}
        onInteraction={onLayerInteraction}
        onEdit={handleEditStep}
        isLastLayerAdded={isLastLayerAdded}
        onOpenAddStepButtons={() => setAddStepButtonsOpened(step.id())}
        onCloseAddStepButtons={() => setAddStepButtonsOpened(null)}
        lastOpened={lastAddStepButtonsOpened}
        transitionsOn={transitionsOn}
        isLastActiveStep={isLastActiveStep}
        active
        layerErrorMsg={layerErrorMsg}
        lastLayerNotInEditMode={llID}
        actions={actions}
      >
        {layerContent}
      </Step>
    </ErrorBoundary>
  );
};
